"use client";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, dayjs, formatTitleForUrl } from "utils";
import { ShellLink } from "@/src/components";
import { BaseFoldableItem } from "./BasefoldableItem";
import { PriceChangePer } from "./PriceChangePer";
import Translations from "@translate/components/CoPilot/CopilotAgent/TimeLineItem/AlertItem";
import { I18nUtil } from "@utils/i18n";
import { AgentReportLevelEnum } from "@/src/features/api/memberApi";
import { useIsPc } from "@/src/hooks";
import { ROUTER_PATH } from "@/src/config";

const I18n = I18nUtil.createScoped(Translations);

interface AlertItemProps {
  id: string;
  title: string;
  agentName: string;
  agentId: string;
  summary: string;
  category: string;
  level: AgentReportLevelEnum;
  created: number;
  impact?: string;
  isExpanded?: boolean;
  assets?: any[];
  toggleExpand?: () => void;
  onChartClick?: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    link?: string;
    type?: string;
  }) => void;
  sourceLink?: string;
}
export const AlertItem = ({
  agentId,
  id: _id,
  title: _title,
  agentName,
  summary,
  impact,
  created,
  isExpanded = false,
  toggleExpand,
  onChartClick,
  category: _category,
  sourceLink,
  assets,
}: AlertItemProps) => {
  const [isPc] = useIsPc();
  const LeftElement = () => {
    return (
      <div
        className="flex items-center gap-[10px]"
        data-tracking-scope="alert-item-left-element"
      >
        <div className="body_bold_s text-content_aura_lv1_primary flex items-center">
          <Icon name="bell" size={14} className="mr-1" />
          <span>
            {I18n.t("Agent")} {agentName}
          </span>
        </div>
        <div className="body_s text-content_tertiary">
          {dayjs(created).format("HH:mm")}
        </div>
      </div>
    );
  };
  return (
    <BaseFoldableItem
      leftElement={<LeftElement />}
      isOpen={isExpanded}
      onOpenChange={toggleExpand}
      onChartClick={() =>
        onChartClick?.({
          title: _title,
          desc: summary ?? "",
          id: _id,
          link: sourceLink ?? "",
          question: I18n.t("Give me more info about this news"),
          type: "ALERT",
        })
      }
      containerClassName="md:gap-2 gap-2"
      data-tracking-scope="alert-item"
    >
      <div
        className={cn(
          "flex flex-col gap-2 min-h-5 transition-all duration-200",
          isExpanded && "h-fit",
        )}
      >
        <div
          className={cn(
            "title_l text-content_primary",
            !isExpanded && "line-clamp-2 md:h-fit",
          )}
        >
          {_title}
        </div>
        <div>
          {impact && (
            <div
              className={cn(
                "body_s text-content_secondary border-l-2 border-border_accent4_solid pl-3 transition-opacity duration-200",
                !isExpanded && "line-clamp-2",
              )}
            >
              {impact}
            </div>
          )}
        </div>
        {isExpanded && (
          <div
            className={cn(
              "body_output_m text-content_primary transition-opacity duration-200",
            )}
          >
            {summary}
          </div>
        )}
        <div className="flex justify-between items-center mt-1">
          {assets && <PriceChangePer assets={assets} />}
          {isExpanded && sourceLink && agentId !== "8" && (
            <ShellLink
              aria-label="Alert Item"
              data-openreplay-label={`Alert Item Link | ${`${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(sourceLink)}`}`}
              id="lnk-alert-item"
              onClick={(e) => {
                e.stopPropagation();
              }}
              target={isPc ? "_blank" : "_self"}
              href={`${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(sourceLink)}`}
              className="flex items-center px-2 py-1 rounded-[20px] bg-background_white_overlay5 w-fit cursor-pointer"
            >
              <span className="body_m text-content_secondary whitespace-nowrap">
                {agentId === "4" ? I18n.t("View Summary") : I18n.t("Read News")}
              </span>
              <Icon
                name="chevron-right"
                size={16}
                color={ospThemes.colors.content_secondary}
              />
            </ShellLink>
          )}
        </div>
      </div>
    </BaseFoldableItem>
  );
};
